import React, { useMemo } from 'react';
import {
  useGetBatchResolvedCardsQuery,
  ResolvedCard,
  UnresolvedCard,
  BatchResolveRequest,
  DecklistCardInput,
} from 'store/services/cards/decklistApi';

// Input types - what DynamicArticle passes in
export interface ParsedDecklistData {
  title: string;
  author?: string;
  format?: string;
  id?: string;
  mainboard: Array<{
    quantity: number;
    name: string;
    setCode?: string;
  }>;
  sideboard: Array<{
    quantity: number;
    name: string;
    setCode?: string;
  }>;
}

// Output types - what Decklist receives
export interface ResolvedDecklistData {
  title: string;
  author?: string;
  format?: string;
  id?: string;
  mainboard: Array<{
    quantity: number;
    name: string;
    setCode?: string;
  }>;
  sideboard: Array<{
    quantity: number;
    name: string;
    setCode?: string;
  }>;
  preResolvedData: {
    mainboard: {
      resolved: ResolvedCard[];
      unresolved: UnresolvedCard[];
    };
    sideboard: {
      resolved: ResolvedCard[];
      unresolved: UnresolvedCard[];
    };
  };
}

interface DecklistBatchResolverProps {
  decklists: ParsedDecklistData[];
  children: (props: {
    resolvedDecklists: ResolvedDecklistData[];
    isLoading: boolean;
    error: string | null;
  }) => React.ReactNode;
}

/**
 * Create a unique key for card deduplication
 * Cards with the same name and set are considered duplicates
 */
function getCardKey(card: { name: string; setCode?: string }): string {
  return `${card.name.toLowerCase()}|${card.setCode?.toLowerCase() || ''}`;
}

/**
 * DecklistBatchResolver - Consolidates multiple decklist card resolution requests into one
 *
 * Problem: Each Decklist component makes 2 API calls (mainboard + sideboard).
 * With 20 decklists, that's 40 concurrent requests that crash the backend.
 *
 * Solution: This component collects all cards from all decklists, deduplicates them,
 * makes ONE API call, then distributes the resolved data back to each decklist.
 */
const DecklistBatchResolver: React.FC<DecklistBatchResolverProps> = ({
  decklists,
  children,
}) => {
  // Step 1: Collect and deduplicate all cards from all decklists
  const allUniqueCards = useMemo(() => {
    const cardMap = new Map<string, DecklistCardInput>();

    for (const decklist of decklists) {
      // Add mainboard cards
      for (const card of decklist.mainboard) {
        const key = getCardKey(card);
        if (!cardMap.has(key)) {
          cardMap.set(key, {
            name: card.name,
            setCode: card.setCode,
            quantity: 1, // Quantity doesn't matter for resolution, we'll restore per-decklist quantities later
          });
        }
      }

      // Add sideboard cards
      for (const card of decklist.sideboard) {
        const key = getCardKey(card);
        if (!cardMap.has(key)) {
          cardMap.set(key, {
            name: card.name,
            setCode: card.setCode,
            quantity: 1,
          });
        }
      }
    }

    return Array.from(cardMap.values());
  }, [decklists]);

  // Step 2: Create memoized request payload for RTK Query
  const batchRequest: BatchResolveRequest = useMemo(
    () => ({
      cards: allUniqueCards,
      includePrice: true,
    }),
    [allUniqueCards]
  );

  // Step 3: Make ONE consolidated API call
  const {
    data: batchResponse,
    isLoading,
    error: queryError,
  } = useGetBatchResolvedCardsQuery(batchRequest, {
    skip: allUniqueCards.length === 0,
  });

  // Step 4: Create lookup maps for fast access
  const resolvedLookup = useMemo(() => {
    if (!batchResponse) return new Map<string, ResolvedCard>();

    const lookup = new Map<string, ResolvedCard>();
    for (const card of batchResponse.resolved) {
      // Match by requestedName (what we sent) - backend returns this for matching
      const key = `${card.requestedName.toLowerCase()}|${card.setCode?.toLowerCase() || ''}`;
      lookup.set(key, card);
    }
    return lookup;
  }, [batchResponse]);

  const unresolvedLookup = useMemo(() => {
    if (!batchResponse) return new Map<string, UnresolvedCard>();

    const lookup = new Map<string, UnresolvedCard>();
    for (const card of batchResponse.unresolved) {
      const key = `${card.requestedName.toLowerCase()}|${card.requestedSet?.toLowerCase() || ''}`;
      lookup.set(key, card);
    }
    return lookup;
  }, [batchResponse]);

  // Step 5: Distribute resolved data back to each decklist
  const resolvedDecklists = useMemo((): ResolvedDecklistData[] => {
    if (!batchResponse) {
      // Return empty preResolvedData while loading
      return decklists.map((decklist) => ({
        ...decklist,
        preResolvedData: {
          mainboard: { resolved: [], unresolved: [] },
          sideboard: { resolved: [], unresolved: [] },
        },
      }));
    }

    return decklists.map((decklist) => {
      // Match mainboard cards
      const mainboardResolved: ResolvedCard[] = [];
      const mainboardUnresolved: UnresolvedCard[] = [];

      for (const card of decklist.mainboard) {
        const key = getCardKey(card);
        const resolved = resolvedLookup.get(key);

        if (resolved) {
          // Clone and restore the original quantity for this decklist
          mainboardResolved.push({
            ...resolved,
            quantity: card.quantity,
          });
        } else {
          const unresolved = unresolvedLookup.get(key);
          if (unresolved) {
            mainboardUnresolved.push({
              ...unresolved,
              quantity: card.quantity,
            });
          } else {
            // Card wasn't found in either resolved or unresolved - treat as unresolved
            mainboardUnresolved.push({
              requestedName: card.name,
              requestedSet: card.setCode,
              quantity: card.quantity,
              reason: 'NOT_FOUND',
              message: `Card "${card.name}" not found`,
            });
          }
        }
      }

      // Match sideboard cards
      const sideboardResolved: ResolvedCard[] = [];
      const sideboardUnresolved: UnresolvedCard[] = [];

      for (const card of decklist.sideboard) {
        const key = getCardKey(card);
        const resolved = resolvedLookup.get(key);

        if (resolved) {
          sideboardResolved.push({
            ...resolved,
            quantity: card.quantity,
          });
        } else {
          const unresolved = unresolvedLookup.get(key);
          if (unresolved) {
            sideboardUnresolved.push({
              ...unresolved,
              quantity: card.quantity,
            });
          } else {
            sideboardUnresolved.push({
              requestedName: card.name,
              requestedSet: card.setCode,
              quantity: card.quantity,
              reason: 'NOT_FOUND',
              message: `Card "${card.name}" not found`,
            });
          }
        }
      }

      return {
        ...decklist,
        preResolvedData: {
          mainboard: {
            resolved: mainboardResolved,
            unresolved: mainboardUnresolved,
          },
          sideboard: {
            resolved: sideboardResolved,
            unresolved: sideboardUnresolved,
          },
        },
      };
    });
  }, [decklists, batchResponse, resolvedLookup, unresolvedLookup]);

  const error = queryError ? 'Failed to load card data' : null;

  return <>{children({ resolvedDecklists, isLoading, error })}</>;
};

export default DecklistBatchResolver;
